import { Component, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { InvoiceCaptureService } from '../../invoice-capture/invoice-capture.service';
import { AuthenticatorService } from '@aws-amplify/ui-angular';
import { CaptureJobInvoice } from '../../api-client/model/captureJobInvoice';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-invoice-list',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    ToastModule,
    TranslateModule,
    FormsModule,
    DropdownModule,
  ],
  providers: [MessageService],
  templateUrl: './invoice-list.component.html',
  styleUrl: './invoice-list.component.css',
})
export class InvoiceListComponent implements OnInit {
  private readonly invoiceCapture = inject(InvoiceCaptureService);
  private readonly authService = inject(AuthenticatorService);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  invoices = signal<CaptureJobInvoice[]>([]);
  isLoading = signal(true);
  error = signal<string | null>(null);
  resumeJobId = signal<number | null>(null);

  languages = [
    { label: 'English', value: 'en' },
    { label: 'Finnish', value: 'fi' },
  ];
  selectedLanguage = this.languages[0];

  ngOnInit(): void {
    this.initializeLanguage();
    this.loadInvoices();
  }

  initializeLanguage(): void {
    const savedLang = localStorage.getItem('lang');
    const langValue =
      savedLang && this.languages.find(l => l.value === savedLang) ? savedLang : 'en';

    this.selectedLanguage = this.languages.find(l => l.value === langValue)!;
    this.translate.addLangs(this.languages.map(l => l.value));
    this.translate.setDefaultLang('en');
    this.translate.use(langValue);
  }

  onLanguageChange(lang: { label: string; value: string }) {
    this.selectedLanguage = lang;
    localStorage.setItem('lang', lang.value);
    this.translate.use(lang.value);
  }

  private async loadInvoices(): Promise<void> {
    try {
      const jobs = await this.invoiceCapture.getJobsByStatus(['pending', 'in_progress', 'saved']);

      const enrichedJobs = jobs.map(job => ({
        ...job,
        file_name: job.invoice_s3_key?.split('/').pop() ?? '',
      }));

      this.invoices.set(enrichedJobs);

      const resumeJob = await this.invoiceCapture.getJob();
      this.resumeJobId.set(resumeJob?.job_id ?? null);
    } catch {
      this.error.set('Failed to load invoices.');
    } finally {
      this.isLoading.set(false);
    }
  }

  viewInvoice(invoice: CaptureJobInvoice) {
    const supportedJobId = this.resumeJobId();
    if (invoice.job_id === supportedJobId) {
      this.router.navigate(['/main']);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('invoiceList.unsupportedJobSummary'),
        detail: this.translate.instant('invoiceList.unsupportedJobDetail', {
          id: this.resumeJobId(),
        }),
      });
    }
  }

  signOut() {
    this.authService.signOut();
  }
}
